import {
  CuratedMedicineConcise,
  MedicineCategory,
  MedicineWithKeyAcg,
} from '@/lib/apis/types'
import React, { useCallback, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil } from '@fortawesome/pro-solid-svg-icons'
import TextButton from '@/shared/components/buttons/TextButton'
import { DialogModal } from '@/shared/components/DialogModal'
import {
  MedicineList,
  type MedicineSelection,
} from '@/features/prescriptions/quiz/MedicineQuizScreen'
import Link from 'next/link'
import { PricingValue } from '@/lib/cms/pricing'

type DatabaseMedicineDisplayProps = {
  medicine: CuratedMedicineConcise
  category: MedicineCategory
  medicinesUnderCategory: Array<MedicineWithKeyAcg>
  preferredBrand?: string
  onUpdateMedicine: (
    medicine: CuratedMedicineConcise,
    preferredBrand?: string,
  ) => void
  variant?: 'default' | 'quiz'
  hideActions?: boolean
  medicineSelectDialogOpen?: boolean
  onMedicineSelectDialogOpenChange?: (open: boolean) => void
}

export default function DatabaseMedicineDisplay({
  medicine,
  medicinesUnderCategory,
  category,
  preferredBrand,
  onUpdateMedicine,
  variant = 'default',
  hideActions = false,
  medicineSelectDialogOpen,
  onMedicineSelectDialogOpenChange,
}: DatabaseMedicineDisplayProps) {
  const [internalMedicineSelectDialogOpen, setInternalMedicineSelectDialogOpen] =
    useState(false)
  const { displayName, quantity, quantifier } = medicine
  const showMedicineSelectDialog =
    medicineSelectDialogOpen ?? internalMedicineSelectDialogOpen

  function setShowMedicineSelectDialog(open: boolean) {
    if (medicineSelectDialogOpen === undefined) {
      setInternalMedicineSelectDialogOpen(open)
    }
    onMedicineSelectDialogOpenChange?.(open)
  }

  if (variant === 'quiz') {
    return (
      <div className="flex flex-col gap-3">
        <div className="rounded-2xl bg-[#F4F4F6] p-4 sm:px-5 sm:py-[18px]">
          <div className="flex items-start gap-5">
            <div className="min-w-0 flex-1">
              <h3 className="text-[15px] font-semibold leading-5 sm:text-[16.5px] sm:leading-[22px] sm:tracking-[-0.01em]">
                {displayName}
              </h3>
              <p className="mt-0.5 text-[12.5px] leading-[17px] text-[#82828B] sm:leading-[18px]">
                {category.name} ·{' '}
                {hideActions
                  ? `${quantity} per script · no repeats`
                  : `${quantity} ${quantifier}`}
              </p>
              <p className="mt-1 text-[12.5px] leading-[17px] text-[#5C5C64] sm:leading-[18px]">
                Preferred brand —{' '}
                <span className="font-medium text-[#0B0B0F]">
                  {preferredBrand || 'No preference'}
                </span>
              </p>
            </div>
            <div className="hidden shrink-0 flex-col items-end sm:flex">
              <p className="text-[20px] font-bold leading-[26px] tracking-[-0.01em]">
                <PricingValue name="prescription" />
              </p>
              <p className="text-[12.5px] leading-4 text-[#82828B]">
                consult fee
              </p>
            </div>
          </div>

          {!hideActions && (
            <div className="mt-3 flex items-baseline gap-2 border-t border-[#E2E2E7] pt-3 sm:mt-3.5 sm:items-center sm:gap-3.5 sm:pt-3.5">
              <div className="flex items-baseline gap-2 sm:hidden">
                <p className="text-[16px] font-bold leading-[21px] tracking-[-0.01em]">
                  <PricingValue name="prescription" />
                </p>
                <p className="text-[12.5px] leading-4 text-[#82828B]">
                  consult fee
                </p>
              </div>
              <button
                className="ml-auto text-[12.5px] font-semibold leading-[17px] text-[#2563EB] transition-colors duration-300 hover:text-[#1D4ED8] sm:ml-0 sm:flex sm:h-9 sm:items-center sm:justify-center sm:rounded-full sm:border-[1.5px] sm:border-[#0B0B0F38] sm:px-4 sm:text-[13.5px] sm:leading-[17px] sm:text-[#0B0B0F] sm:transition-[border-color,background-color] sm:hover:border-[#0B0B0F66] sm:hover:bg-white sm:hover:text-[#0B0B0F]"
                onClick={() => setShowMedicineSelectDialog(true)}
                type="button"
              >
                <span className="hidden sm:inline">
                  Choose a different medicine
                </span>
                <span className="sm:hidden">Change →</span>
              </button>
              <Link
                className="hidden text-[13.5px] font-semibold leading-[18px] text-[#2563EB] transition-colors duration-300 hover:text-[#1D4ED8] sm:block"
                href="/prescriptions-online/categories"
              >
                Browse all categories →
              </Link>
            </div>
          )}
          {hideActions && (
            <div className="mt-3 flex items-baseline gap-2 border-t border-[#E2E2E7] pt-3 sm:hidden">
              <p className="text-[16px] font-bold leading-[21px] tracking-[-0.01em]">
                <PricingValue name="prescription" />
              </p>
              <p className="text-[12.5px] leading-4 text-[#82828B]">
                consult fee
              </p>
            </div>
          )}
        </div>

        <DialogModal
          show={showMedicineSelectDialog}
          onClose={() => setShowMedicineSelectDialog(false)}
          width="3xl"
          motionPreset="editorial"
          panelClasses="!flex !max-h-[calc(100svh-2rem)] !flex-col !overflow-hidden !rounded-[24px] !p-0 sm:!max-h-[calc(100svh-4rem)]"
        >
          <MedicineSelectDialog
            category={category}
            medicinesUnderCategory={medicinesUnderCategory}
            onClose={() => setShowMedicineSelectDialog(false)}
            onSelectMedicine={(nextMedicine, nextPreferredBrand) => {
              onUpdateMedicine(nextMedicine, nextPreferredBrand)
              setShowMedicineSelectDialog(false)
            }}
          />
        </DialogModal>
      </div>
    )
  }

  return (
    <div className="mb-2 flex flex-col gap-y-1.5">
      <div className="flex w-full flex-col rounded-lg border bg-gray-100 px-5 py-3 text-gray-700 transition-colors">
        <h3 className="font-bitter text-lg font-semibold">{displayName}</h3>
        <div className="mt-3.5 grid grid-cols-1 gap-x-2 sm:grid-cols-4 sm:gap-y-1">
          <div className="col-span-1 text-sm text-gray-600">Quantity</div>
          <div className="col-span-1 font-medium text-gray-800 sm:col-span-3">
            {quantity} {quantifier}
          </div>
          <div className="col-span-1 mt-2.5 text-sm text-gray-600 sm:mt-0">
            Preferred brand
          </div>
          <div className="col-span-1 truncate font-medium text-gray-800 sm:col-span-3">
            {preferredBrand ? preferredBrand : 'No preference'}
          </div>
        </div>
      </div>

      <TextButton
        title={
          <span className="block w-full py-1">
            <FontAwesomeIcon icon={faPencil} className="mr-2" /> Select
            something else
          </span>
        }
        color="blue"
        onClick={() => setShowMedicineSelectDialog(true)}
        className="grow"
      />

      <DialogModal
        show={showMedicineSelectDialog}
        onClose={() => setShowMedicineSelectDialog(false)}
        width="3xl"
        motionPreset="editorial"
        panelClasses="!flex !max-h-[calc(100svh-2rem)] !flex-col !overflow-hidden !rounded-[24px] !p-0 sm:!max-h-[calc(100svh-4rem)]"
      >
        <MedicineSelectDialog
          category={category}
          medicinesUnderCategory={medicinesUnderCategory}
          onClose={() => setShowMedicineSelectDialog(false)}
          onSelectMedicine={(medicine, preferredBrand) => {
            onUpdateMedicine(medicine, preferredBrand)
            setShowMedicineSelectDialog(false)
          }}
        />
      </DialogModal>
    </div>
  )
}

type MedicineSelectDialogProps = {
  category: MedicineCategory
  medicinesUnderCategory: Array<MedicineWithKeyAcg>
  onClose: () => void
  onSelectMedicine: (
    medicine: CuratedMedicineConcise,
    preferredBrand?: string,
  ) => void
}

function MedicineSelectDialog({
  category,
  medicinesUnderCategory,
  onClose,
  onSelectMedicine,
}: MedicineSelectDialogProps) {
  const [pendingSelection, setPendingSelection] =
    useState<MedicineSelection | null>(null)
  const handleSelectionChange = useCallback(
    (selection: MedicineSelection | null) => setPendingSelection(selection),
    [],
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col font-sans text-[#0B0B0F]">
      <div className="flex shrink-0 items-start gap-4 border-b border-[#E2E2E7] bg-white px-5 pb-4 pt-5 sm:px-7 sm:pb-5 sm:pt-6">
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[21px] font-bold leading-[27px] tracking-[-0.02em] sm:text-[26px] sm:leading-8">
            Choose a different medicine.
          </h2>
          <p className="mt-1 text-[13px] leading-[18px] text-[#82828B] sm:text-[13.5px] sm:leading-[19px]">
            Showing medicines for {category.name}
          </p>
        </div>
        <button
          aria-label="Close medicine selection"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F4F4F6] text-[#5C5C64] transition-colors duration-300 hover:bg-[#ECECEF] hover:text-[#0B0B0F]"
          onClick={onClose}
          type="button"
        >
          <XMarkIcon className="h-[17px] w-[17px] stroke-2" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-1 sm:px-7 sm:pb-7 sm:pt-1.5">
        <MedicineList
          category={category}
          groups={medicinesUnderCategory}
          onSelectionChange={handleSelectionChange}
          showCategoryNavigation={false}
          showConsultationFallback={false}
        />
      </div>
      <div className="shrink-0 border-t border-[#E2E2E7] bg-white px-5 py-4 sm:flex sm:justify-end sm:px-7 sm:py-5">
        <button
          className="flex h-[47px] w-full items-center justify-center rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white transition-[background-color,opacity] duration-300 enabled:hover:bg-[#242428] disabled:cursor-not-allowed disabled:bg-[#A0A0A8] disabled:text-white disabled:opacity-70 sm:w-auto sm:min-w-[164px]"
          disabled={!pendingSelection}
          onClick={() => {
            if (!pendingSelection) return
            onSelectMedicine(
              pendingSelection.medicine,
              pendingSelection.preferredBrand,
            )
          }}
          type="button"
        >
          Confirm selection
        </button>
      </div>
    </div>
  )
}
